<template>
    <HeadMetaGame />
    <Layout>
        <main :class="$style.page">
            <div>
                <GameContainer
                    :game="game"
                    :categories="gameCatInfo"
                    :isGameStarted="isGameStarted"
                    @startPlayingGame="startGame"
                    @stopPlayingGame="stopGame"
                    @openReportModal="setIsReportBugModalOpen(true)"
                />
            </div>
            <GameCardCarousel
                v-if="firstRecommendationsGameBlock"
                :data-interface-section="firstRecommendationsGameBlock.groupType"
                :title="firstRecommendationsGameBlock.title"
                :rows="firstRecommendationsGameBlock.rows"
                :games="firstRecommendationsGameBlock.games"
            />
            <LazyHydration :hydrate="hydrateOnIdle()">
                <GameDetails
                    v-if="game && gameCatInfo"
                    :game="game"
                    :gameCatInfo="gameCatInfo"
                />
                <GameCardCarousel
                    v-if="showRelatedGames && hasGameSeries"
                    data-interface-section="related"
                    :title="showRecommendationsTitle('related')"
                    :games="relatedGames"
                />

                <GameCardCarousel
                    v-if="showSpotlightGames"
                    data-interface-section="top_playgama"
                    :title="showRecommendationsTitle('top_playgama')"
                    :games="spotlightGames"
                    big
                />
                <GameCardCarousel
                    v-if="showRecommendedGames"
                    data-interface-section="recommended"
                    :title="showRecommendationsTitle('recommended')"
                    :games="recommendedGames"
                />
                <GameCardCarousel
                    v-if="showRandomGames"
                    data-interface-section="random"
                    :title="showRecommendationsTitle('random')"
                    :games="randomGames"
                />
                <PromoCategories />
                <TheEnd />
                <Breadcrumbs :items="breadcrumps" />
            </LazyHydration>
            <LazyHydration :hydrate="hydrateOnIdle()">
                <ModalReportBug
                    :isOpen="isReportModalOpen"
                    :page="route.fullPath"
                    @closeReportModal="setIsReportBugModalOpen(false)"
                />
            </LazyHydration>
        </main>
    </Layout>
</template>
<script lang="ts" setup>
import { onServerPrefetch, onMounted, onBeforeUnmount, ref, computed, hydrateOnIdle, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useCategoriesStore } from '@/store/categories-store'
import Layout from '@/layouts/ModernTwoColumnsLayout.vue'
import TheEnd from '@/components_new/TheEnd/TheEnd.vue'
import GameCardCarousel from '@/components_new/GameCardCarousel.vue'
import PromoCategories from '@/components_new/PromoCategories/PromoCategories.vue'
import { useLayoutStore } from '@/store/layout-store'
import ModalReportBug from '@/components_new/ModalItems/components/ModalReportBug.vue'
import LazyHydration from '@/components_new/LazyHydration.vue'
import HeadMetaGame from '@/pages/Games/components/HeadMetaGame.vue'
import Breadcrumbs from '@/components_new/Breadcrumbs.vue'
import { useGamePageStore } from './game-store'
import GameContainer from './components/GameContainer.vue'
import GameDetails from './components/GameDetails.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { useSetPageData } from '@/utils/vue-hooks/use-set-page-data'
import { useTr } from '@/composables/use-tr'
import { useAppStore } from '@/store/app-store'
import { useUrl } from '@/composables/url-helpers'
import { useLoggerStore } from '@/store/logger'
import { useI18nAddon } from '@/i18n/i18n'

const categoriesStore = useCategoriesStore()
const gamePageStore = useGamePageStore()
const loggerStore = useLoggerStore()
const route = useRoute()
const router = useRouter()
const tr = useTr()
const appStore = useAppStore()
const { currentLocale } = useI18nAddon()

const { game, recommendations, gameCatInfo } = storeToRefs(gamePageStore)
const layoutStore = useLayoutStore()
const { setIsReportBugModalOpen } = layoutStore

const isGameStarted = ref(false)
const hasGameSeries = computed(() => game.value?.series?.length)
const seriesGames = computed(() => recommendations.value?.series)

const firstRecommendationsGameBlock = computed(() => {
    if (hasGameSeries.value && seriesGames.value?.length) {
        const seriesTitle = gameCatInfo.value?.categoriesInfo?.find((cat) => cat.categoryType === 'series')?.title || ''
        return {
            title: seriesTitle,
            games: seriesGames.value,
            groupType: 'series',
            rows: 1,
        }
    }
    if (showRelatedGames.value) {
        return {
            title: showRecommendationsTitle('related'),
            games: relatedGames.value,
            groupType: 'related',
        }
    }
    return null
})

const breadcrumps = computed(() => {
    const categoryPath = gameCatInfo.value?.pathCategories.map((cat) => ({ title: cat.title, href: cat.href })) ?? []

    return game?.value
        ? [
              {
                  title: tr('game.main_title'),
                  href: { name: ROUTE_NAMES.MAIN },
              },
              ...categoryPath,
              {
                  title: game.value?.title,
                  href: { name: ROUTE_NAMES.GAME_PAGE, params: { game: game.value?.hru } },
                  disabled: true,
              },
          ]
        : []
})

const isReportModalOpen = computed(() => layoutStore.isReportBugModalOpen)
const relatedGames = computed(() => recommendations.value?.related)
const showRelatedGames = computed(() => (relatedGames.value ? relatedGames.value.length > 0 : true))
const spotlightGames = computed(() => recommendations.value?.top_playgama)
const showSpotlightGames = computed(() => (spotlightGames.value ? spotlightGames.value.length > 0 : true))
const recommendedGames = computed(() => recommendations.value?.recommended)
const showRecommendedGames = computed(() => (recommendedGames.value ? recommendedGames.value.length > 0 : true))
const randomGames = computed(() => recommendations.value?.random)
const showRandomGames = computed(() => (randomGames.value ? randomGames.value.length > 0 : true))

type RecommendationTitle = 'related' | 'top_playgama' | 'recommended' | 'random'
function showRecommendationsTitle(title: RecommendationTitle): string {
    switch (title) {
        case 'related':
            return tr('game.related')
        case 'top_playgama':
            return tr('game.top_playgama')
        case 'recommended':
            return tr('game.recommended')
        case 'random':
            return tr('game.random')
    }
}

function startGame() {
    isGameStarted.value = true
}

function stopGame() {
    isGameStarted.value = false
}

async function updateData(routeHRU: string) {
    // Prevents the request from being made twice if the data is already in the store
    if (!gamePageStore.isDataValid(routeHRU)) {
        isGameStarted.value = false
        gamePageStore.resetGameData()
        await gamePageStore.setGameWithRecommendations(routeHRU)
    }
}

const { getCategoryRoute } = useUrl()

function processRedirectResponse() {
    const { gameRedirect } = gamePageStore
    if (!gameRedirect) {
        return
    }
    const { redirectTarget, status: statusCode } = gameRedirect

    let redirectURL = ''

    if (redirectTarget.type === 'game') {
        redirectURL = router.resolve({
            name: ROUTE_NAMES.GAME_PAGE,
            params: { game: redirectTarget.game_hru },
        }).href
    } else if (redirectTarget.type === 'category') {
        redirectURL = router.resolve(
            getCategoryRoute({ hru: redirectTarget.cat_hru, type: redirectTarget.cat_type }),
        ).href
    } else if (redirectTarget.type === 'main-page') {
        redirectURL = router.resolve({
            name: ROUTE_NAMES.MAIN,
        }).href
    }

    appStore.setPageStatus(statusCode)
    appStore.setTargetURL(redirectURL)
}

onServerPrefetch(async () => {
    await Promise.all([updateData(route.params.game as string), categoriesStore.setMainData()])
    processRedirectResponse()
})

useSetPageData(() => game.value && { content_category: game.value.categories, provider: game.value.source || '' })

onMounted(async () => {
    await Promise.all([updateData(route.params.game as string), categoriesStore.setMainData()])
    processRedirectResponse()
    if (game.value) {
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'game_preview',
            gameCategories: game.value.categories,
        })
    }
})

onBeforeUnmount(() => {
    gamePageStore.resetGameData()
})

watch(currentLocale, async () => {
    await Promise.all([updateData(route.params.game as string), categoriesStore.setMainData()])
    processRedirectResponse()
})
</script>
<style module>
.page {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

/*
    Style for empty Ad block.
    Cannot not be just "display: none" because Ad container needs to be visible to react on size change.
*/
.emptyAd {
    margin-top: -24px;
}
</style>
